// StepSection.js
import React, { useEffect, useRef, useState } from "react";
import {dashboardL,
	adsInsight,
	actionableInsights,
	productGrowth,
	salesInsight,
	timeSale,
  stepBg, } from "../../../../../images/index.js";
import { motion, AnimatePresence } from "motion/react";
import { Link } from "react-router-dom";

const features = [
  {
    title: "Smart Dashboard",
    description:
      "Get clear steps on what to fix, what to scale, and where to improve.",
    image: dashboardL,
  },
  {
    title: "Ads Performance Analyzer",
    description:
      "Get clear steps on what to fix, what to scale, and where to improve.",
    image: adsInsight,
  },
  {
    title: "Geo Sales Insights",
    description:
      "Get clear steps on what to fix, what to scale, and where to improve.",
    image: salesInsight,
  },
  {
    title: "Time-based Performance",
    description:
      "Get clear steps on what to fix, what to scale, and where to improve.",
    image: timeSale,
  },
  {
    title: "Product Growth Tracker",
    description:
      "Get clear steps on what to fix, what to scale, and where to improve.",
    image: productGrowth,
  },
  {
    title: "Action Recommendations",
    description:
      "Get clear steps on what to fix, what to scale, and where to improve.",
    image: actionableInsights,
  },
];

const StepSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [dotTop, setDotTop] = useState("-10px");

  const featureRefs = useRef([]);
  const containerRef = useRef(null);
  const sliderRef = useRef(null);

  // 🟣 Smoothly calculate the dot's vertical position
  useEffect(() => {
    if (!containerRef.current || !featureRefs.current[activeIndex]) return;

    const activeEl = featureRefs.current[activeIndex];
    const containerTop =
      containerRef.current.getBoundingClientRect().top + window.scrollY;
    const elementTop = activeEl.getBoundingClientRect().top + window.scrollY;
    const offset = activeIndex === 0 ? 10 : 30;

    const newTop = `${elementTop - containerTop - 30 + offset}px`;
    setDotTop(newTop);
  }, [activeIndex]);

  // ✅ Scroll listener: pick the item closest to viewport center (fixes “jump to 0”)
  useEffect(() => {
    const handleScroll = () => {
      let closestIndex = 0;
      let minDistance = Infinity;
      const middleY = window.scrollY + window.innerHeight / 2;

      featureRefs.current.forEach((el, index) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const elementY = rect.top + window.scrollY + rect.height / 2;
        const distance = Math.abs(elementY - middleY);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      setActiveIndex(closestIndex);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // 🔹 Handle mobile horizontal slider
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;
    const handleScroll = () => {
      const scrollLeft = slider.scrollLeft;
      const cardWidth = slider.scrollWidth / features.length;
      const index = Math.round(scrollLeft / cardWidth);
      setActiveIndex(Math.min(index, features.length - 1));
    };
    slider.addEventListener("scroll", handleScroll);
    return () => slider.removeEventListener("scroll", handleScroll);
  }, []);

    const floatingItems = [
    {
      text: "User Friendly Visualization",
      className: "floating-tag top-left",
    },
    {
      text: "Actionable Alerts",
      className: "floating-tag top-right",
    },
    {
      text: "Comprehensive Coverage",
      className: "floating-tag bottom-left",
    },
    {
      text: "Real-Time Insights",
      className: "floating-tag bottom-right",
    },
  ];

  return (
    <>
      <div className="how-it-works-section text-white d-flex flex-column">
        <div className="d-flex gap-3 mb-5 flex-wrap">
          <button className="btn hero-btn px-4 py-3 mb-5 me-md-3">
          <Link to={"/signin"} className='text-decoration-none'>Imagine Your Amazon </Link></button>
        </div>

        <motion.h2
          className="hero-heading mb-3"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut",delay:0.1 }}
          viewport={{ once: false }}
        >
          From “Maybe” to “Actionable”        </motion.h2>
        <motion.p className="hero-sub-text mt-4"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut",delay:0.2}}
          viewport={{ once: false }}
        >
        Actionable insights that helps you to empower your Amazon strategy, decisions, and time for every department        </motion.p>

        <div className="row mt-5 how-it-works-section-step">
          {/* ---------- LEFT SIDE ---------- */}
          <div className="col-lg-5 position-relative text-start" ref={containerRef}>
            {/* Purple Line */}
            <div
              className="position-absolute"
              style={{
                left: "0",
                top: "-28px",
                bottom: "-25px",
                width: "28px",
                background: "linear-gradient(180deg, #8b5cf6 0%, #a78bfa 100%)",
                borderRadius: "24px",
              }}
            />

            {/* Feature Titles */}
            <div className="ms-5 mt-5">
              {features.map((feature, index) => {
                const isActive = activeIndex === index;
                return (
                  <div
                    key={index}
                    ref={(el) => (featureRefs.current[index] = el)}
                    className={`mb-4 ${index <= 5 ? "mt-4" : "pb-4"}`}
                    style={{
                      background: isActive
                        ? "linear-gradient(342.08deg, #ECC2D8 -91.14%, #362BDF 104.1%)"
                        : "transparent",
                      borderRadius: "24px",
                      padding: isActive ? "56px 36px" : "5px",
                      transition: "all 1.2s cubic-bezier(0.65, 0, 0.35, 1)",
                    }}
                  >
                    <h4
                      className="what-section-left-text mb-2"
                      style={{
                        opacity: isActive ? 1 : 0.5,
                        transition: "opacity 0.8s ease-in-out",
                      }}
                    >
                      {feature.title}
                    </h4>
                    {isActive && (
                      <p className="mb-0 what-section-left-sub-text">
                        {feature.description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 🟣 Purple Dot (slow & smooth both ways) */}
            <div
              className="position-absolute"
              style={{
                left: "0",
                top: dotTop,
                width: "28px",
                height: "28px",
                background:
                  "linear-gradient(342.08deg, #ECC2D8 -91.14%, #362BDF 104.1%)",
                borderRadius: "24px",
                zIndex: 10,
                transition: "top 1.8s cubic-bezier(0.65, 0, 0.35, 1)",
              }}
            />
          </div>

          {/* ---------- RIGHT SIDE (Smooth Image Fade) ---------- */}
          <div className="col-lg-7 m-auto text-center position-relative img-step-container">

          {/* Orbit Rings */}
          <div className="orbit-circle circle-1"></div>
          <div className="orbit-circle circle-2"></div>
          <div className="orbit-circle circle-3"></div>


          {/* Floating labels */}
          {floatingItems.map((item, i) => (
            <motion.div
            key={i}
            className={item.className}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            >
              {item.text}
            </motion.div>
          ))}

          {/* Main Image */}
          <div className="image-frame-wrapper" >
            <AnimatePresence mode="wait">
              <motion.img
                key={features[activeIndex].image}
                src={features[activeIndex].image}
                alt={features[activeIndex].title}
                className="frame-content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              />
                  <motion.div className="orbit-bg-svg" style={{ backgroundImage: `url(${stepBg})` }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.4, ease: "easeInOut" }}
                  ></motion.div>
            </AnimatePresence>
          </div>
          </div>
        </div>
      </div>
      <div className="mobile-slider-wrapper">
        {/* Cards Slider */}
        <motion.div
          className="cards-wrapper"
          ref={sliderRef}
          onScroll={() => {
            if (sliderRef.current) {
              scrollX.set(sliderRef.current.scrollLeft);
            }
          }}
        >
          <div className="cards-track">
            {features.map((card, index) => (
              <motion.div
                key={index}
                className={`card ${index === activeIndex ? "active" : ""}`}
              >
                <h3 className="card-title">{card.title}</h3>
                <p className="card-description">{card.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
        <div className="text-center mb-4">
          <motion.img
            key={features[activeIndex].image}
            src={features[activeIndex].image}
            alt={features[activeIndex].title}
            className="img-fluid step-image"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>
    </>
  );
};

export default StepSection;
